import { useState, useRef, useEffect, useCallback } from 'react';
import { 
  Play, 
  Pause, 
  Volume2, 
  VolumeX, 
  Maximize, 
  SkipBack, 
  SkipForward,
  Settings,
  CheckCircle,
  XCircle,
  RefreshCw,
  FileText,
  Subtitles,
} from 'lucide-react';

/**
 * Interactive Video Player
 * Supports embedded questions that pause the video at specific timestamps
 * Similar to Skillsoft Percipio
 */
export default function InteractiveVideoPlayer({
  videoUrl,
  captionsUrl = null,
  transcript = null,
  questions = [],
  initialPosition = 0,
  onProgress,
  onQuestionAnswer,
  onComplete,
}) {
  const videoRef = useRef(null);
  const progressRef = useRef(null);
  const overlayRef = useRef(null);
  
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(initialPosition);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [showSpeedMenu, setShowSpeedMenu] = useState(false);
  const [showCaptions, setShowCaptions] = useState(false);
  
  // Question overlay state
  const [activeQuestion, setActiveQuestion] = useState(null);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [matchSelections, setMatchSelections] = useState({});
  const [reflectionText, setReflectionText] = useState('');
  const [answerResult, setAnswerResult] = useState(null);
  const [answeredQuestions, setAnsweredQuestions] = useState(new Set());
  
  // Track watched segments for progress
  const [watchedSegments, setWatchedSegments] = useState([]);
  
  // Sort questions by timestamp
  const sortedQuestions = [...questions].sort((a, b) => a.timestamp - b.timestamp);
  const resetQuestionsFromTime = (time) => {
    const threshold = Number(time || 0);
    setAnsweredQuestions((prev) => {
      if (!prev.size) return prev;
      const next = new Set();
      sortedQuestions.forEach((q) => {
        if (prev.has(q.id) && Number(q.timestamp) < threshold) {
          next.add(q.id);
        }
      });
      return next;
    });
  };
  const getNextUnansweredAtTimestamp = (timestamp, excludeId = null) => {
    const target = Math.floor(timestamp);
    return sortedQuestions.find((q) => {
      if (excludeId && q.id === excludeId) return false;
      return Math.floor(q.timestamp) === target && !answeredQuestions.has(q.id);
    });
  };
  
  // Check for questions at current time
  useEffect(() => {
    if (activeQuestion) return;
    
    const currentQuestion = sortedQuestions.find(q => {
      const questionTime = Math.floor(q.timestamp);
      const current = Math.floor(currentTime);
      return questionTime === current && !answeredQuestions.has(q.id);
    });
    
    if (currentQuestion) {
      // Always pause — pauseVideo flag just means "pause" vs "overlay while playing"
      // For simplicity and reliability, always pause at question timestamps
      videoRef.current?.pause();
      setIsPlaying(false);
      setActiveQuestion(currentQuestion);
      setSelectedOptions([]);
      setMatchSelections({});
      setReflectionText('');
      setAnswerResult(null);
    }
  }, [currentTime, sortedQuestions, answeredQuestions, activeQuestion]);
  
  // Handle video time update
  const handleTimeUpdate = () => {
    const video = videoRef.current;
    if (!video) return;
    
    setCurrentTime(video.currentTime);
    
    // Track watched segments
    const segment = Math.floor(video.currentTime / 5) * 5; // 5-second segments
    if (!watchedSegments.includes(segment)) {
      setWatchedSegments(prev => [...prev, segment]);
    }
    
    // Report progress
    if (onProgress) {
      const watchedSeconds = watchedSegments.length * 5;
      onProgress({
        currentPosition: video.currentTime,
        watchedSeconds: Math.min(watchedSeconds, video.duration),
        totalSeconds: video.duration,
        percentage: Math.round((watchedSeconds / video.duration) * 100),
      });
    }
  };
  
  // Handle video loaded
  const handleLoadedMetadata = () => {
    const video = videoRef.current;
    if (!video) return;
    
    setDuration(video.duration);
    
    // Resume from initial position
    if (initialPosition > 0) {
      video.currentTime = initialPosition;
    }
  };
  
  // Handle video ended
  const handleEnded = () => {
    setIsPlaying(false);
    if (onComplete) {
      onComplete();
    }
  };
  
  // Play/Pause toggle
  const togglePlay = () => {
    const video = videoRef.current;
    if (!video || activeQuestion) return;
    
    if (video.paused) {
      video.play();
      setIsPlaying(true);
    } else {
      video.pause();
      setIsPlaying(false);
    }
  };
  
  // Seek
  const handleSeek = (e) => {
    if (activeQuestion) return;
    
    const rect = progressRef.current.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    const newTime = percent * duration;
    
    if (newTime < currentTime) {
      // Re-arm checkpoints from the seek point forward so users can review.
      resetQuestionsFromTime(newTime);
    }
    videoRef.current.currentTime = newTime;
    setCurrentTime(newTime);
  };
  
  // Skip forward/back
  const skip = (seconds) => {
    if (activeQuestion) return;
    const video = videoRef.current;
    const newTime = Math.max(0, Math.min(video.currentTime + seconds, duration));
    if (newTime < video.currentTime) {
      // Re-arm checkpoints from this rewind point forward.
      resetQuestionsFromTime(newTime);
    }
    video.currentTime = newTime;
  };
  
  // Volume
  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    videoRef.current.volume = newVolume;
    setIsMuted(newVolume === 0);
  };
  
  const toggleMute = () => {
    const video = videoRef.current;
    if (isMuted) {
      video.volume = volume || 1;
      setIsMuted(false);
    } else {
      video.volume = 0;
      setIsMuted(true);
    }
  };
  
  // Fullscreen
  const toggleFullscreen = () => {
    const container = videoRef.current?.parentElement?.parentElement;
    if (!container) return;
    
    if (!document.fullscreenElement) {
      container.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };
  
  // Playback speed
  const changeSpeed = (speed) => {
    videoRef.current.playbackRate = speed;
    setPlaybackSpeed(speed);
    setShowSpeedMenu(false);
  };
  
  // Format time
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  
  // Handle option selection
  const handleOptionSelect = (optionId) => {
    if (answerResult) return;
    if (activeQuestion.questionType === 'drag_and_match') return;
    
    if (activeQuestion.questionType === 'multiple_select') {
      setSelectedOptions(prev => 
        prev.includes(optionId)
          ? prev.filter(id => id !== optionId)
          : [...prev, optionId]
      );
    } else {
      setSelectedOptions([optionId]);
    }
  };
  
  // Submit answer
  const handleSubmitAnswer = async () => {
    const isOpenResponse = activeQuestion.questionType === 'reflection' || activeQuestion.questionType === 'long_text';
    const isDragMatch = activeQuestion.questionType === 'drag_and_match';
    if (!isOpenResponse && !isDragMatch && selectedOptions.length === 0) return;
    if (isOpenResponse && !reflectionText.trim()) return;
    if (isDragMatch) {
      const expected = (activeQuestion.options || []).filter((o) => o.id).length;
      if (Object.keys(matchSelections || {}).length < expected) return;
    }
    
    try {
      // Call parent handler to submit to API
      const response = isOpenResponse
        ? [reflectionText.trim()]
        : isDragMatch
          ? Object.entries(matchSelections).map(([leftId, rightText]) => `${leftId}::${rightText}`)
          : selectedOptions;
      const result = await onQuestionAnswer(activeQuestion.id, response);
      
      setAnswerResult(result);
      const shouldRewind = !result.isCorrect && Number.isFinite(Number(result.rewindTo));
      if (result.isCorrect) {
        setAnsweredQuestions(prev => new Set([...prev, activeQuestion.id]));
      }
      
      // If incorrect and rewind is enabled, wait then rewind
      if (shouldRewind) {
        setTimeout(() => {
            const rewindTarget = Number(result.rewindTo);
            // Remove question from answeredQuestions so it can reappear
            setAnsweredQuestions(prev => {
              const next = new Set(prev);
              next.delete(activeQuestion.id);
              return next;
            });
            videoRef.current.currentTime = rewindTarget;
            setCurrentTime(rewindTarget);
            setActiveQuestion(null);
            setAnswerResult(null);
          }, 3000);
      }
    } catch (error) {
      console.error('Failed to submit answer:', error);
    }
  };

  const handleRetry = () => {
    setAnswerResult(null);
    setSelectedOptions([]);
    setReflectionText('');
  };
  
  // Continue after question
  const handleContinue = () => {
    if (answerResult && !answerResult.isCorrect) {
      setAnswerResult(null);
      setSelectedOptions([]);
      setMatchSelections({});
      setReflectionText('');
      return;
    }

    const currentQuestion = activeQuestion;
    const nextAtSameTimestamp = currentQuestion
      ? getNextUnansweredAtTimestamp(currentQuestion.timestamp, currentQuestion.id)
      : null;

    setAnswerResult(null);
    setSelectedOptions([]);
    setMatchSelections({});
    setReflectionText('');

    if (nextAtSameTimestamp) {
      setActiveQuestion(nextAtSameTimestamp);
      return;
    }

    setActiveQuestion(null);
    
    // Resume video
    setTimeout(() => {
      videoRef.current?.play();
      setIsPlaying(true);
    }, 100);
  };
  
  // Calculate question markers positions
  const questionMarkers = sortedQuestions.map(q => ({
    id: q.id,
    position: (q.timestamp / duration) * 100,
    answered: answeredQuestions.has(q.id),
  }));
  
  // Auto-hide controls
  useEffect(() => {
    let timeout;
    if (isPlaying && !activeQuestion) {
      timeout = setTimeout(() => setShowControls(false), 3000);
    }
    return () => clearTimeout(timeout);
  }, [isPlaying, showControls, activeQuestion]);

  // Move focus into knowledge-check overlay so keyboard/AT users are aware of it
  useEffect(() => {
    if (activeQuestion && overlayRef.current) {
      overlayRef.current.focus();
    }
  }, [activeQuestion]);

  // Keyboard control for the progress-bar slider
  const handleProgressKeyDown = (e) => {
    if (activeQuestion) return;
    if (e.key === 'ArrowRight' || e.key === 'ArrowUp') { e.preventDefault(); skip(5); }
    if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') { e.preventDefault(); skip(-5); }
    if (e.key === 'Home') { e.preventDefault(); videoRef.current.currentTime = 0; setCurrentTime(0); }
    if (e.key === 'End') { e.preventDefault(); videoRef.current.currentTime = duration; setCurrentTime(duration); }
  };

  return (
    <div className="space-y-3">
    <div 
      className="relative bg-black rounded-lg overflow-hidden group"
      onMouseMove={() => setShowControls(true)}
      onMouseLeave={() => isPlaying && setShowControls(false)}
    >
      {/* Video */}
      <video
        ref={videoRef}
        src={videoUrl}
        className="w-full aspect-video"
        aria-label="Course video"
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={handleEnded}
        onClick={togglePlay}
      >
        {captionsUrl && (
          <track
            kind="captions"
            src={captionsUrl}
            srcLang="en"
            label="English"
          />
        )}
      </video>
      
      {/* Question Overlay — full-cover, no popup card */}
      {activeQuestion && (
        <div
          ref={overlayRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby="kc-question-heading"
          tabIndex={-1}
          className="absolute inset-0 z-20 flex flex-col outline-none"
          style={{ animation: 'fadeSlideUp 0.25s ease-out' }}
        >
          {/* Dark gradient background */}
          <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900" />

          {/* Content */}
          <div className="relative z-10 flex flex-col h-full px-8 py-6 overflow-y-auto">
            {/* Header */}
            <div className="mb-5">
              <span id="kc-question-heading" className="text-xs font-semibold tracking-widest uppercase text-primary-300">Knowledge Check</span>
            </div>

            {!answerResult ? (
              <div className="flex flex-col flex-1 justify-center max-w-2xl w-full mx-auto">
                <h3 className="text-xl font-bold text-white leading-snug mb-6">
                  {activeQuestion.question}
                </h3>
                {activeQuestion.questionType === 'multiple_select' && (
                  <p className="text-sm text-white/60 -mt-4 mb-5">Select all that apply</p>
                )}
                {activeQuestion.questionType === 'drag_and_match' && (
                  <p className="text-sm text-white/60 -mt-4 mb-5">Match each prompt to the best answer</p>
                )}

                {/* Reflection / Long text */}
                {(activeQuestion.questionType === 'reflection' || activeQuestion.questionType === 'long_text') ? (
                  <textarea
                    value={reflectionText}
                    onChange={e => setReflectionText(e.target.value)}
                    className="w-full bg-white/10 border border-white/20 rounded-xl p-4 text-white placeholder-white/40 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400 resize-none mb-5"
                    rows={5}
                    placeholder="Write your response here…"
                  />
                ) : activeQuestion.questionType === 'drag_and_match' ? (
                  <div className="space-y-3 mb-6">
                    {(activeQuestion.options || []).map((option, idx) => {
                      const matchPool = (activeQuestion.matchPool && activeQuestion.matchPool.length)
                        ? activeQuestion.matchPool
                        : Array.from(new Set((activeQuestion.options || []).map((o) => o.matchText).filter(Boolean)));
                      const selectedValue = matchSelections[option.id] || '';
                      return (
                        <div key={option.id || idx} className="rounded-xl border border-white/20 bg-white/10 p-3">
                          <div className="text-sm text-white mb-2">{option.text}</div>
                          <select
                            value={selectedValue}
                            onChange={(e) => setMatchSelections((prev) => ({ ...prev, [option.id]: e.target.value }))}
                            aria-label={`Match for: ${option.text}`}
                            className="w-full rounded-lg bg-gray-900/80 border border-white/20 text-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
                          >
                            <option value="">Select match...</option>
                            {matchPool.map((candidate, cIdx) => (
                              <option key={`${option.id}-${cIdx}`} value={candidate}>{candidate}</option>
                            ))}
                          </select>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="space-y-3 mb-6">
                    {activeQuestion.options.map((option) => (
                      <button
                        key={option.id}
                        onClick={() => handleOptionSelect(option.id)}
                        aria-pressed={selectedOptions.includes(option.id)}
                        className={`w-full text-left px-5 py-4 rounded-xl border transition-all duration-150 ${
                          selectedOptions.includes(option.id)
                            ? 'bg-primary-500/90 border-primary-400 text-white shadow-lg scale-[1.01]'
                            : 'bg-white/10 border-white/20 text-white hover:bg-white/20 hover:border-white/40'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-5 h-5 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${
                            selectedOptions.includes(option.id) ? 'border-white bg-white' : 'border-white/50'
                          }`}>
                            {selectedOptions.includes(option.id) && (
                              <div className="w-2 h-2 bg-primary-500 rounded-full" />
                            )}
                          </div>
                          <span className="text-sm font-medium">{option.text}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                )}

                <button
                  onClick={handleSubmitAnswer}
                  disabled={
                    (activeQuestion.questionType === 'reflection' || activeQuestion.questionType === 'long_text')
                      ? !reflectionText.trim()
                      : activeQuestion.questionType === 'drag_and_match'
                        ? Object.keys(matchSelections || {}).length < (activeQuestion.options || []).filter((o) => o.id).length
                        : selectedOptions.length === 0
                  }
                  className="w-full bg-primary-500 hover:bg-primary-600 disabled:opacity-40 disabled:cursor-not-allowed text-white font-semibold py-4 rounded-xl transition-all duration-150 text-sm"
                >
                  Submit Answer
                </button>
              </div>
            ) : (
              <div className="flex flex-col flex-1 justify-center max-w-2xl w-full mx-auto items-center text-center">
                {answerResult.isCorrect ? (
                  <>
                    <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mb-4" aria-hidden="true">
                      <CheckCircle className="w-10 h-10 text-green-400" />
                    </div>
                    <h3 className="text-2xl font-bold text-green-400 mb-2">Correct!</h3>
                  </>
                ) : (
                  <>
                    <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mb-4" aria-hidden="true">
                      <XCircle className="w-10 h-10 text-red-400" />
                    </div>
                    <h3 className="text-2xl font-bold text-red-400 mb-2">Not Quite</h3>
                  </>
                )}
                <p className="text-white/80 text-base mb-5 max-w-md">{answerResult.feedback}</p>

                {answerResult.explanation && (
                  <div className="bg-white/10 border border-white/20 rounded-xl px-6 py-4 mb-6 max-w-md text-left">
                    <p className="text-sm text-white/70">{answerResult.explanation}</p>
                  </div>
                )}

                {(!answerResult.isCorrect && Number.isFinite(Number(answerResult.rewindTo))) ? (
                  <div className="flex items-center gap-2 text-amber-400 text-sm" role="status" aria-live="polite">
                    <RefreshCw className="w-4 h-4" aria-hidden="true" />
                    <span>Rewinding to review that section…</span>
                  </div>
                ) : (
                  <button onClick={handleContinue} className="bg-primary-500 hover:bg-primary-600 text-white font-semibold px-10 py-4 rounded-xl transition-all duration-150 text-sm">
                    {answerResult.isCorrect ? 'Continue' : 'Try Again'}
                  </button>
                )}
              </div>
            )}
          </div>

          <style>{`
            @keyframes fadeSlideUp {
              from { opacity: 0; transform: translateY(12px); }
              to   { opacity: 1; transform: translateY(0); }
            }
          `}</style>
        </div>
      )}
      
      {/* Play/Pause Overlay (center) */}
      {!isPlaying && !activeQuestion && (
        <button
          onClick={togglePlay}
          aria-label="Play video"
          className="absolute inset-0 flex items-center justify-center bg-black/30 z-10"
        >
          <div className="w-20 h-20 rounded-full bg-white/90 flex items-center justify-center" aria-hidden="true">
            <Play className="w-10 h-10 text-gray-900 ml-1" />
          </div>
        </button>
      )}
      
      {/* Controls */}
      <div className={`absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4 transition-opacity ${
        showControls || !isPlaying ? 'opacity-100' : 'opacity-0'
      }`}>
        {/* Progress bar */}
        <div 
          ref={progressRef}
          role="slider"
          tabIndex={0}
          aria-label="Video progress"
          aria-valuemin={0}
          aria-valuemax={Math.round(duration) || 0}
          aria-valuenow={Math.round(currentTime)}
          aria-valuetext={`${formatTime(currentTime)} of ${formatTime(duration)}`}
          className="relative h-1 bg-white/30 rounded-full mb-3 cursor-pointer group/progress"
          onClick={handleSeek}
          onKeyDown={handleProgressKeyDown}
        >
          {/* Buffered */}
          <div className="absolute h-full bg-white/50 rounded-full" style={{ width: '100%' }} />
          
          {/* Played */}
          <div 
            className="absolute h-full bg-primary-500 rounded-full"
            style={{ width: `${(currentTime / duration) * 100}%` }}
          />
          
          {/* Question markers */}
          {questionMarkers.map(marker => (
            <div
              key={marker.id}
              aria-hidden="true"
              className={`absolute top-1/2 -translate-y-1/2 w-3 h-3 rounded-full border-2 border-white ${
                marker.answered ? 'bg-green-500' : 'bg-amber-500'
              }`}
              style={{ left: `${marker.position}%`, marginLeft: '-6px' }}
            />
          ))}
          
          {/* Hover thumb */}
          <div 
            aria-hidden="true"
            className="absolute top-1/2 -translate-y-1/2 w-4 h-4 bg-white rounded-full opacity-0 group-hover/progress:opacity-100 transition-opacity"
            style={{ left: `${(currentTime / duration) * 100}%`, marginLeft: '-8px' }}
          />
        </div>
        
        {/* Controls row */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {/* Play/Pause */}
            <button onClick={togglePlay} aria-label={isPlaying ? 'Pause' : 'Play'} className="p-2 text-white hover:text-primary-400">
              {isPlaying ? <Pause size={20} aria-hidden="true" /> : <Play size={20} aria-hidden="true" />}
            </button>
            
            {/* Skip back */}
            <button onClick={() => skip(-10)} aria-label="Skip back 10 seconds" className="p-2 text-white hover:text-primary-400">
              <SkipBack size={18} aria-hidden="true" />
            </button>
            
            {/* Skip forward */}
            <button onClick={() => skip(10)} aria-label="Skip forward 10 seconds" className="p-2 text-white hover:text-primary-400">
              <SkipForward size={18} aria-hidden="true" />
            </button>
            
            {/* Volume */}
            <div className="flex items-center gap-1 group/volume">
              <button onClick={toggleMute} aria-label={isMuted ? 'Unmute' : 'Mute'} className="p-2 text-white hover:text-primary-400">
                {isMuted ? <VolumeX size={18} aria-hidden="true" /> : <Volume2 size={18} aria-hidden="true" />}
              </button>
              <input
                type="range"
                min="0"
                max="1"
                step="0.1"
                value={isMuted ? 0 : volume}
                onChange={handleVolumeChange}
                aria-label="Volume"
                className="w-0 group-hover/volume:w-20 transition-all opacity-0 group-hover/volume:opacity-100"
              />
            </div>
            
            {/* Time */}
            <span className="text-white text-sm ml-2">
              {formatTime(currentTime)} / {formatTime(duration)}
            </span>
          </div>
          
          <div className="flex items-center gap-2">
            {/* CC toggle — only when captionsUrl is provided */}
            {captionsUrl && (
              <button
                onClick={() => {
                  const tracks = videoRef.current?.textTracks;
                  if (!tracks || !tracks.length) return;
                  const next = !showCaptions;
                  setShowCaptions(next);
                  for (let i = 0; i < tracks.length; i++) {
                    tracks[i].mode = next ? 'showing' : 'hidden';
                  }
                }}
                aria-label={showCaptions ? 'Turn off captions' : 'Turn on captions'}
                aria-pressed={showCaptions}
                className={`p-2 text-sm font-bold rounded leading-none ${
                  showCaptions ? 'text-primary-400 bg-white/10' : 'text-white hover:text-primary-400'
                }`}
              >
                CC
              </button>
            )}
            {/* Speed */}
            <div className="relative">
              <button 
                onClick={() => setShowSpeedMenu(!showSpeedMenu)}
                aria-label={`Playback speed ${playbackSpeed}x`}
                aria-haspopup="listbox"
                aria-expanded={showSpeedMenu}
                className="p-2 text-white hover:text-primary-400 text-sm"
              >
                {playbackSpeed}x
              </button>
              {showSpeedMenu && (
                <div role="listbox" aria-label="Playback speed" className="absolute bottom-full right-0 mb-2 bg-gray-900 rounded-lg py-1 min-w-[80px]">
                  {[0.5, 0.75, 1, 1.25, 1.5, 2].map(speed => (
                    <button
                      key={speed}
                      role="option"
                      aria-selected={playbackSpeed === speed}
                      onClick={() => changeSpeed(speed)}
                      className={`block w-full px-4 py-1 text-sm text-left ${
                        playbackSpeed === speed ? 'text-primary-400' : 'text-white hover:bg-gray-800'
                      }`}
                    >
                      {speed}x
                    </button>
                  ))}
                </div>
              )}
            </div>
            
            {/* Fullscreen */}
            <button onClick={toggleFullscreen} aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'} className="p-2 text-white hover:text-primary-400">
              <Maximize size={18} aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>
    </div>
    {transcript && (
      <details className="border border-gray-200 rounded-lg bg-gray-50">
        <summary className="cursor-pointer select-none px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg flex items-center gap-2 list-none [&::-webkit-details-marker]:hidden">
          <FileText size={14} aria-hidden="true" /> View transcript
        </summary>
        <div className="px-4 pb-4 pt-2 max-h-64 overflow-y-auto">
          <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">{transcript}</p>
        </div>
      </details>
    )}
    </div>
  );
}

